// src/components/AdminPanel/AdminFaqs.js
import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { adminApi } from '../../services/adminApi';

// The key under which the My Gatherings FAQ HTML is stored.
// MyGatherings.js reads from the same key.
const CONTENT_KEY = 'my_gatherings_faq';

// Default FAQ content shown the first time an admin loads the editor and
// nothing has been saved yet. Mirrors the existing hardcoded FAQ on
// MyGatherings.js so admins can tweak it from a known starting point.
const DEFAULT_FAQ_HTML = `
<h3>Q1. What should I expect from the gathering?</h3>
<p>A1. This is a unique experience where you'll be joining a gathering / group in a welcoming and friendly environment. It's an amazing opportunity to grow your social circle in London!</p>

<h3>Q2. Where is the venue (pub/bar) for my gathering? 📍</h3>
<p>A2. The venue details are shown on your booking on this page (okgather.com/my-gatherings) — see the venue line on each gathering above. Remember to check your spam folder for any emails just in case.</p>

<h3>Q3. How do I find my gathering when I arrive? 🔎</h3>
<p>A3. Upon arrival, simply ask a staff member at the bar for the table booked under the name "OkGather" (there is one gathering / group table per venue).</p>

<h3>Q4. How many people will be at the gathering?</h3>
<p>A4. In general we aim for a smaller group you can easily chat to rather than a large, overwhelming event. We think it's the best way to really get to know people and make connections. 😊</p>

<h3>Q5. Will the pub/bar be the same as my last gathering?</h3>
<p>A5. No – it will often be a different location, as we use many different venues. Always check your booking on this page (okgather.com/my-gatherings) for the current venue. Please don't go to the same pub/bar as your previous gathering without checking first.</p>

<h3>Q6. What if I have feedback, suggestions, or any issues? 💡</h3>
<p>A6. Please email us at <a href="mailto:info@okgather.com">info@okgather.com</a>. We'd love to hear from you and will do our best to help really quickly! This is the best and fastest way to get a response.</p>

<h3>Q7. Can I be allocated to the same gathering as my friend? 👥</h3>
<p>A7. Yes! Simply ask your friend to also sign up at OkGather.com for the same slot/date, then let us know their email address by contacting us at <a href="mailto:info@okgather.com">info@okgather.com</a> and we'll put you both in the same gathering! 😊</p>

<h3>Q8. What is a "connection"? 🤝</h3>
<p>A8. After a gathering, you can add people you got on with as a connection so you can find them again and plan to meet up separately. Connections are only visible to subscribers.</p>

<h3>Q9. What's the difference between a hangout I create and an official gathering? 📅</h3>
<p>A9. A self-run hangout (found under "Plan a Hangout" on your My Gatherings page) is one you organize yourself with your connections or circles - it's not run or curated by OkGather. Official gatherings are the ones OkGather books the venue for, and those still require an active subscription to book.</p>

<h3>Q10. Can I invite someone who isn't on OkGather to my hangout? 👋</h3>
<p>A10. Yes, if you make your hangout "public" you'll get a shareable link that anyone can use to RSVP, no OkGather account needed. This is only for hangouts you create yourself - it doesn't apply to OkGather's official gatherings.</p>

<p>We hope you meet some great people at your next gathering! Well done for taking a big step toward growing your social circle! 🎉</p>
`.trim();

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

function AdminFaqs({ adminToken }) {
  const [contentHtml, setContentHtml] = useState('');
  const [originalHtml, setOriginalHtml] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await adminApi.getSiteContent(CONTENT_KEY);
        if (cancelled) return;
        const html = data.content_html && data.content_html.trim() ? data.content_html : DEFAULT_FAQ_HTML;
        setContentHtml(html);
        setOriginalHtml(html);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    if (!adminToken) return;
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      await adminApi.updateSiteContent(CONTENT_KEY, contentHtml, adminToken);
      setOriginalHtml(contentHtml);
      setSuccess('FAQ saved. Users will see the updated FAQ on the My Gatherings page.');
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    if (!window.confirm('Replace current content with the default FAQ template? This will not save until you click Save.')) return;
    setContentHtml(DEFAULT_FAQ_HTML);
  };

  const isDirty = contentHtml !== originalHtml;

  return (
    <div>
      <h2>FAQs (My Gatherings page)</h2>
      <div style={{
        backgroundColor: '#eff6ff',
        border: '1px solid #3b82f6',
        borderRadius: '8px',
        padding: '0.75rem 1rem',
        marginBottom: '1.25rem',
        fontSize: '0.9rem',
        color: '#1e3a8a',
        lineHeight: '1.5',
      }}>
        Edit the FAQ block that appears on the <strong>My Gatherings</strong> page.
        Use the toolbar for formatting. Click <strong>Save</strong> to publish — changes
        are visible to users immediately on next page load.
      </div>

      {error && <p style={{ color: 'red' }}>{error}</p>}
      {success && <p style={{ color: 'green' }}>{success}</p>}

      {loading ? (
        <p>Loading FAQ…</p>
      ) : (
        <>
          <div style={{ backgroundColor: '#fff', border: '1px solid #d1d5db', borderRadius: '6px', marginBottom: '1rem' }}>
            <ReactQuill
              theme="snow"
              value={contentHtml}
              onChange={setContentHtml}
              modules={QUILL_MODULES}
              style={{ minHeight: '320px' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
            <button
              onClick={handleSave}
              disabled={saving || !isDirty}
              style={{
                padding: '0.5rem 1.25rem',
                backgroundColor: isDirty ? '#16a34a' : '#9ca3af',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: isDirty && !saving ? 'pointer' : 'not-allowed',
                fontWeight: '600',
              }}
            >
              {saving ? 'Saving…' : isDirty ? 'Save FAQ' : 'Saved'}
            </button>
            <button
              onClick={handleResetToDefault}
              type="button"
              style={{ padding: '0.5rem 1.25rem', backgroundColor: '#6b7280', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              Reset to default template
            </button>
          </div>

          <h3 style={{ marginTop: '2rem' }}>Preview (how users will see it)</h3>
          <div
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              padding: '1rem 1.25rem',
              backgroundColor: '#fafafa',
              color: '#4B5563',
              lineHeight: '1.6',
            }}
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
        </>
      )}
    </div>
  );
}

export default AdminFaqs;
